import React from "react";
import moment from "moment";
import { compose } from "redux";
import { List } from "immutable";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import { EMPTY_LIST } from "app/app.constants";
import CalendarDay from "app/components/CalendarYear/CalendarDay";
import CalendarPlaceholderDay from "app/components/CalendarYear/CalendarPlaceholderDay";

const dateIsWithinDateRange = (date, startDate, endDate) =>
  date.isBetween(startDate, endDate, "day", "[]");

const isSelected = (date, selectedStartDate, selectedEndDate) =>
  selectedStartDate && selectedEndDate
    ? dateIsWithinDateRange(date, selectedStartDate, selectedEndDate)
    : false;

const datesIncludeDate = (dateToLookup, dates = EMPTY_LIST) =>
  dates.some(date => date.isSame(dateToLookup, "day"));

const CalendarRange = ({
  end,
  month,
  start,
  dayWidth,
  DayComponent,
  onSelectDates,
  availableDates,
  selectedEndDate,
  setSelectedRange,
  selectedStartDate,
  DateRangeComponent,
  dateRange: dateRanges,
  DateRangeEndComponent,
  DateRangeStartComponent,
  DateRangeComponentProps,
}) => {
  const actualRangeDates = List(moment.range(start, end).by("day"));

  const dateRangeStartDates = dateRanges.map(dateRange =>
    dateRange.get("startDate"),
  );
  const dateRangeEndDates = dateRanges.map(dateRange =>
    dateRange.get("endDate"),
  );

  const dates = availableDates
    .filter(ad => datesIncludeDate(ad, actualRangeDates))
    .groupBy(date => date.month() === month);

  const datesInDateRangeMatchingMonth = dates.get(true, EMPTY_LIST);
  const datesNotMatchingMonth = dates.get(false, EMPTY_LIST);

  const datesWithinDateRange = datesInDateRangeMatchingMonth.filter(date => {
    if (datesIncludeDate(date, dateRangeStartDates))
      return !DateRangeStartComponent;
    if (datesIncludeDate(date, dateRangeEndDates))
      return !DateRangeEndComponent;

    return true;
  });

  const matchesEndDate = datesInDateRangeMatchingMonth.find(date =>
    datesIncludeDate(date, dateRangeEndDates),
  );

  const matchesStartDate = datesInDateRangeMatchingMonth.find(date =>
    datesIncludeDate(date, dateRangeStartDates),
  );

  let displayStart = matchesEndDate
    ? matchesStartDate && !matchesStartDate.isSame(matchesEndDate, "day")
    : matchesStartDate;

  const classes = useStyles({ dayWidth, datesWithinDateRange });

  return (
    <>
      {datesNotMatchingMonth
        .filter(date => date.isBefore(datesInDateRangeMatchingMonth.first()))
        .map((date, index) => (
          <CalendarPlaceholderDay key={index} width={dayWidth} />
        ))}
      {DateRangeStartComponent && displayStart && (
        <CalendarDay
          date={start}
          month={month}
          width={dayWidth}
          dateRanges={dateRanges}
          onSelectDates={onSelectDates}
          setSelectedRange={setSelectedRange}
          DateRangeStartComponent={DateRangeStartComponent}
          isSelected={isSelected(start, selectedStartDate, selectedEndDate)}
        />
      )}
      <Grid className={classes.root}>
        {datesWithinDateRange.map((date, index) => (
          <CalendarDay
            date={date}
            key={index}
            month={month}
            DayComponent={DayComponent}
            onSelectDates={onSelectDates}
            setSelectedRange={setSelectedRange}
            isSelected={isSelected(date, selectedStartDate, selectedEndDate)}
          />
        ))}
        {DateRangeComponent && (
          <DateRangeComponent
            endDate={end}
            startDate={start}
            dateRanges={dateRanges}
            {...DateRangeComponentProps}
          />
        )}
      </Grid>
      {DateRangeEndComponent && matchesEndDate && (
        <CalendarDay
          date={end}
          month={month}
          width={dayWidth}
          dateRanges={dateRanges}
          displayStart={displayStart}
          onSelectDates={onSelectDates}
          setSelectedRange={setSelectedRange}
          DateRangeEndComponent={DateRangeEndComponent}
          isSelected={isSelected(end, selectedStartDate, selectedEndDate)}
        />
      )}
      {datesNotMatchingMonth
        .filter(date => date.isAfter(datesInDateRangeMatchingMonth.last()))
        .map((date, index) => (
          <CalendarPlaceholderDay key={index} width={dayWidth} />
        ))}
    </>
  );
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    position: "relative",
    width: ({ dayWidth, datesWithinDateRange }) =>
      `calc(${dayWidth} * ${datesWithinDateRange.size})`,
  },
});

const styles = () => ({
  calendarRangeRoot: {
    display: "flex",
    position: "relative",
  },
});

export default compose(
  React.memo,
  withStyles(styles),
)(CalendarRange);
